import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export default function FeedbackTab() {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('Calculation Issue');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Load email automatically if user is logged in or entered before
  useEffect(() => {
    const savedEmail = localStorage.getItem('utilix_feedback_email');
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setIsSubmitting(true);
    
    // Simulate real database / EmailJS integration forward
    setTimeout(() => {
      try {
        // Log feedback in local storage persistent cache for webmaster records
        const key = 'utilix_user_feedbacks';
        const existing = localStorage.getItem(key);
        const feedbacks = existing ? JSON.parse(existing) : [];
        feedbacks.push({
          id: Date.now(),
          category,
          description,
          email,
          timestamp: new Date().toISOString(),
          siloUrl: window.location.href
        });
        localStorage.setItem(key, JSON.stringify(feedbacks));

        // Save candidate contact email
        if (email) {
          localStorage.setItem('utilix_feedback_email', email);
        }

        setIsSubmitting(false);
        setSubmitStatus('success');
        setDescription('');
      } catch (err) {
        setIsSubmitting(false);
        setSubmitStatus('error');
      }
    }, 1200);
  };

  return (
    <>
      {/* Sticky Tab Button on Right Edge */}
      <button
        id="btn-sticky-feedback-trigger"
        onClick={() => {
          setIsOpen(true);
          setSubmitStatus('idle');
        }}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-50 bg-[#00e699] text-black font-black text-[11px] uppercase tracking-widest py-4 px-3 rounded-l-2xl shadow-2xl flex items-center gap-2 transform origin-right rotate-180 hover:bg-[#00e699]/90 transition-all hover:pr-4"
        style={{ writingMode: 'vertical-rl' }}
      >
        <MessageSquare className="w-4 h-4 transform rotate-90 shrink-0" />
        <span>Report Issue / Support</span>
      </button>

      {/* Slide-In Side Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Guard overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-[#000] z-[100]"
            />

            {/* Slide-Out Main Menu Box */}
            <motion.div
              initial={{ translateX: '100%' }}
              animate={{ translateX: 0 }}
              exit={{ translateX: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#1e1f20] border-l-4 border-slate-900 z-[101] shadow-2xl p-8 flex flex-col justify-between overflow-y-auto"
              style={{ contentVisibility: 'auto' } as any}
            >
              <div className="space-y-6">
                
                {/* Header info */}
                <div className="flex items-center justify-between border-b border-slate-800/60 pb-5">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-[#00e699]/10 text-[#00e699] flex items-center justify-center">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-white font-black text-sm uppercase tracking-tight">Complaint Protocol</h3>
                      <p className="text-[10px] text-slate-400">Institutional Feedback Sandbox Dispatch</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-slate-900 rounded-xl text-slate-400 hover:text-white transition-all active:scale-90"
                    aria-label="Close feedback drawer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {submitStatus === 'success' ? (
                  <div className="space-y-6 text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-[#00e699] flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-10 h-10 animate-bounce" />
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-white font-black text-lg uppercase tracking-tight">Dispatch Complete!</h4>
                      <p className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
                        Your submission has been forwarded to the webmaster queue. If required, we will contact you directly via the specified electronic address.
                      </p>
                    </div>
                    <button
                      onClick={() => setSubmitStatus('idle')}
                      className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-widest px-6 py-3 rounded-xl border border-slate-800 transition-all active:scale-95"
                    >
                      New Report
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    
                    {/* Category Selector dropdown */}
                    <div className="space-y-2">
                      <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-400">Issue Category</label>
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full bg-[#0b0b0b] border-2 border-slate-800 rounded-xl px-4 py-3 text-sm text-[#e3e3e3] focus:outline-none focus:border-[#00e699] font-medium"
                      >
                        <option value="Calculation Issue">🔢 Calculation Issue</option>
                        <option value="UI Bug">🐛 UI Bug</option>
                        <option value="Feature Request">💡 Feature Request</option>
                        <option value="General Complaint">⚠️ General Complaint</option>
                      </select>
                    </div>

                    {/* Contact Email field */}
                    <div className="space-y-2">
                      <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-400">Contact Email (Optional)</label>
                      <input
                        type="email"
                        value={email}
                        placeholder="yourname@domain.com"
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-[#0b0b0b] border-2 border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00e699]"
                      />
                    </div>

                    {/* Detailed Description textarea */}
                    <div className="space-y-2">
                      <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-400">Describe the Issue</label>
                      <textarea
                        required
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Please supply explicit parameters or steps to reproduce the calculation anomaly..."
                        rows={6}
                        className="w-full bg-[#0b0b0b] border-2 border-slate-800 rounded-xl p-4 text-sm text-white focus:outline-none focus:border-[#00e699] leading-relaxed resize-none font-medium placeholder-gray-600"
                      />
                    </div>

                    {submitStatus === 'error' && (
                      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center space-x-3 text-xs">
                        <AlertCircle className="w-4 h-4" />
                        <span>Transmission error. Please check your network and retry.</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting || !description.trim()}
                      className="w-full bg-[#00e699] text-black font-black uppercase tracking-widest text-xs py-4 rounded-xl hover:bg-[#00e699]/90 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#00e699]/10"
                    >
                      <Send className="w-4 h-4" />
                      <span>{isSubmitting ? 'Transmitting Data...' : 'Submit Diagnostics'}</span>
                    </button>

                  </form>
                )}

              </div>

              {/* Disclaimer */}
              <div className="border-t border-slate-800/40 pt-5 mt-6 text-[10px] text-gray-500 leading-relaxed">
                <span className="font-bold text-slate-400 block mb-1">🔒 Client Workspace Protection</span>
                Telemetry values and current page states are packaged locally in your browser context. No external unauthorized profiles or servers are contacted.
              </div>

            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
